import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Granny emergency-safe mobile drawer controller.
 * The original animation-based close could leave <html scroll-lock> active
 * if the drawer disappeared before animationend fired. This version opens and
 * closes deterministically and always unlocks scroll on close.
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    const summary = this.refs.details?.querySelector('summary');
    summary?.addEventListener('click', this.#onSummaryClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    const summary = this.refs.details?.querySelector('summary');
    summary?.removeEventListener('click', this.#onSummaryClick);
  }

  #onSummaryClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.toggle(undefined, event);
  };

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open') || this.refs.details.classList.contains('menu-open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  toggle(target, event) {
    return this.isOpen ? this.close() : this.open(target, event);
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');
    if (!summary) return;

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');
    this.#lockScroll();

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (target) this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      try { trapFocus(details); } catch (_) {}
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer?.classList.remove('menu-drawer--has-submenu-opened');

    if (details === this.refs.details) {
      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);
      removeTrapFocus();
    } else {
      reset(details);
      try { trapFocus(this.refs.details); } catch (_) {}
    }

    reset(details);
    this.#unlockScroll();
  }

  #lockScroll() {
    document.documentElement.setAttribute('scroll-lock', '');
    document.documentElement.classList.add('scroll-lock');
    document.body.classList.add('scroll-lock');
  }

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.classList.remove('scroll-lock');
    document.body.classList.remove('scroll-lock');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');
    content.forEach((element) => {
      if (element instanceof HTMLElement) element.classList.add('details-content--no-animation');
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) element.classList.remove('details-content--no-animation');
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

// Extra safety: any close/removal path must never leave the store frozen.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('.menu-drawer__close-button, .menu-drawer__backdrop, a[href]')) {
    const details = target.closest('header-drawer')?.querySelector('details');
    if (details && !details.classList.contains('menu-open')) {
      document.documentElement.removeAttribute('scroll-lock');
      document.documentElement.classList.remove('scroll-lock');
      document.body.classList.remove('scroll-lock');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }
}, true);
